import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from '../i18n/LocalizationProvider';
import type { TranslationContent } from '../i18n/translations';
import { getStore, issueTests } from '../data/portalStore';
import { useAuth } from '../auth/AuthContext';
import type { PortalStore } from '../data/portalStore';

export default function AdminPage() {
  const { t } = useTranslation();
  const labels = t<TranslationContent['admin']>('admin');
  const { session } = useAuth();
  const [store, setStore] = useState<PortalStore>(() => getStore());
  const [issueAmount, setIssueAmount] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    setStore(getStore());
  }, []);

  const org = store.orgs[0];
  const user = store.users.find(item => item.id === org?.userId);

  const issuedLedger = useMemo(
    () => store.ledger.filter(entry => entry.type === 'issue').slice(0, 6),
    [store.ledger]
  );

  const handleIssue = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setMessage('');
    const amount = Number(issueAmount);
    if (!org || !session || Number.isNaN(amount) || amount <= 0) {
      setMessage(labels.issue.error);
      return;
    }
    const result = issueTests(org.id, amount, session.userId);
    if (result.error) {
      setMessage(labels.issue.error);
      return;
    }
    setStore(result.store);
    setIssueAmount('');
    setMessage(labels.issue.success);
  };

  if (!org || !user) {
    return (
      <div className="panel">
        <p className="text-muted">{labels.empty}</p>
      </div>
    );
  }

  return (
    <div className="panel">
      <div className="section-heading">
        <div>
          <span className="badge">{labels.badge}</span>
          <h2>{labels.title}</h2>
          <p className="text-muted">{labels.description}</p>
        </div>
        <div className="metric-card" style={{ margin: 0, padding: '1rem 1.25rem' }}>
          <span>{labels.orgSummary.available}</span>
          <strong>{org.availableTests}</strong>
        </div>
      </div>

      <div className="metric-grid" style={{ marginBottom: '2rem' }}>
        <div className="metric-card">
          <span>{labels.orgSummary.org}</span>
          <strong>{org.name}</strong>
        </div>
        <div className="metric-card">
          <span>{labels.orgSummary.user}</span>
          <strong>{user.name}</strong>
        </div>
        <div className="metric-card">
          <span>{labels.orgSummary.totalIssued}</span>
          <strong>{org.totalIssued}</strong>
        </div>
      </div>

      <form
        className="panel"
        onSubmit={handleIssue}
        style={{ boxShadow: 'none', border: '1px dashed var(--color-border)', marginBottom: '2rem' }}
      >
        <h3>{labels.issue.title}</h3>
        <p className="text-muted">{labels.issue.description}</p>
        <div className="form-grid two-cols">
          <label>
            <span className="text-muted">{labels.issue.amountLabel}</span>
            <input
              type="number"
              min={1}
              value={issueAmount}
              onChange={event => setIssueAmount(event.target.value)}
              placeholder={labels.issue.amountPlaceholder}
              required
            />
          </label>
          <button type="submit">{labels.issue.submit}</button>
        </div>
        {message && <p className="text-muted">{message}</p>}
      </form>

      <div className="panel" style={{ boxShadow: 'none' }}>
        <h3>{labels.ledger.title}</h3>
        <table>
          <thead>
            <tr>
              <th>{labels.ledger.table.date}</th>
              <th>{labels.ledger.table.amount}</th>
              <th>{labels.ledger.table.actor}</th>
            </tr>
          </thead>
          <tbody>
            {issuedLedger.map(entry => (
              <tr key={entry.id}>
                <td>{entry.createdAt}</td>
                <td>{entry.amount}</td>
                <td>{entry.actorId}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
